import { useId, type ReactNode } from 'react';
import './ui.css';

interface CardProps {
  title: string;
  subtitle?: string;
  /** Optional anchor target so same-page links can scroll to this card. */
  id?: string;
  /** Header-right slot for filters, links or buttons. */
  actions?: ReactNode;
  /** Removes the body padding so a table can meet the card edges. */
  flush?: boolean;
  children: ReactNode;
}

/**
 * Titled content panel. The heading is wired to the section with
 * `aria-labelledby`, so each card is an addressable landmark for screen-reader
 * navigation rather than an anonymous box.
 */
export function Card({ title, subtitle, id, actions, flush, children }: CardProps) {
  const headingId = useId();

  return (
    <section className="ui-card" id={id} aria-labelledby={headingId}>
      <header className="ui-card__header">
        <div className="ui-card__heading">
          <h2 className="ui-card__title" id={headingId}>
            {title}
          </h2>
          {subtitle ? <p className="ui-card__subtitle">{subtitle}</p> : null}
        </div>
        {actions ? <div className="ui-card__actions">{actions}</div> : null}
      </header>
      <div className={flush ? 'ui-card__body ui-card__body--flush' : 'ui-card__body'}>
        {children}
      </div>
    </section>
  );
}
